import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/authStore'

// Lazy import queryClient to avoid circular dependencies
// The queryClient is exported from main.tsx
let queryClientInstance: { clear: () => void } | null = null
const getQueryClient = async () => {
  if (!queryClientInstance) {
    const { queryClient } = await import('../main')
    queryClientInstance = queryClient
  }
  return queryClientInstance
}

// PERMANENT FIX: Always use the environment variable, never fallback to relative path
const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout to allow for slow external API calls
})

// Track if we're currently refreshing
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (error?: any) => void
}> = []

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Request interceptor for auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // If no response or not 401, reject immediately
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error)
    }

    // Don't retry auth endpoints
    if (originalRequest.url?.includes('/auth/')) {
      return Promise.reject(error)
    }

    // Already retried once
    if (originalRequest._retry) {
      useAuthStore.getState().logout()
      // Clear React Query cache before redirecting
      getQueryClient().then(qc => qc?.clear()).catch(() => {})
      window.location.href = '/login?session_expired=true'
      return Promise.reject(error)
    }

    // If we're already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return apiClient(originalRequest)
        })
        .catch((err) => {
          return Promise.reject(err)
        })
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const refreshed = await useAuthStore.getState().refreshAccessToken()

      if (refreshed) {
        const newToken = useAuthStore.getState().token
        processQueue(null, newToken)
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return apiClient(originalRequest)
      } else {
        processQueue(new Error('Refresh failed'), null)
        useAuthStore.getState().logout()
        // Clear React Query cache before redirecting
        getQueryClient().then(qc => qc?.clear()).catch(() => {})
        window.location.href = '/login?session_expired=true'
        return Promise.reject(error)
      }
    } catch (refreshError) {
      processQueue(refreshError as Error, null)
      useAuthStore.getState().logout()
      // Clear React Query cache before redirecting
      getQueryClient().then(qc => qc?.clear()).catch(() => {})
      window.location.href = '/login?session_expired=true'
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default apiClient
